/**
 * `Timeline` — chronological list of curated outbreak events.
 *
 * Custom component (no third-party timeline library) so we keep full
 * control over RTL flipping, severity colour, and reduced-motion
 * behaviour. Backed by `timeline.list({ slug, cursor, limit: 30 })` via
 * TanStack Query `useInfiniteQuery`.
 *
 * Layout:
 *   - Vertical centre line drawn behind all events.
 *   - On `≥ md` viewports each row becomes a three-column grid:
 *     left content / axis lane / right content. Events alternate sides
 *     while the card content itself remains `text-align: start`, so the
 *     left lane is visibly occupied instead of reading like right-side
 *     metadata pressed against the spine.
 *   - On small viewports events collapse to a single column with the
 *     axis near inline-start.
 *   - Each event fades up via Framer `whileInView`, skipped under
 *     `prefers-reduced-motion`.
 *
 * Markdown: bodies are server-resolved Markdown. We render a small,
 * safe subset inline — bold, italic, links, code spans, paragraphs and
 * lists — without pulling in `react-markdown`.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { MotionConfig, motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { type CSSProperties, Fragment, type ReactElement, type ReactNode, useMemo } from "react";

import type { TimelineEventOut, TimelineListOutput } from "@hantavirus/shared";
import { type Source, SourceCitation } from "#/components/shared/SourceCitation.js";
import { type TranslateFn, formatDate } from "#/lib/i18n.js";

export type TimelineProps = {
  /** Outbreak slug — required. */
  slug: string;
  /** UI locale, propagated as `x-locale` header. */
  locale: string;
  /** Locale-resolved labels. */
  t: TranslateFn;
  /** Page size; defaults to 30 to match the router default. */
  limit?: number;
  /** Optional first page from SSR. */
  initialItems?: TimelineListOutput;
  /** Optional API base override — used by tests. */
  apiBaseUrl?: string;
};

type TimelineSeverity = TimelineEventOut["severity"];

const SEVERITY_TOKEN: Record<TimelineSeverity, string> = {
  urgent: "var(--color-accent-emergency)",
  warning: "var(--color-accent-warning)",
  milestone: "var(--color-accent-info)",
  info: "var(--color-fg-muted)",
};

const EVENT_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
};

function buildApiUrl(base: string | undefined): string {
  const fallback =
    typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined;
  const resolved = (base ?? fallback ?? "").replace(/\/+$/u, "");
  return `${resolved}/api/trpc/timeline.list`;
}

async function fetchTimelinePage(args: {
  slug: string;
  cursor?: number;
  limit: number;
  locale: string;
  apiBaseUrl?: string;
}): Promise<TimelineListOutput> {
  const { slug, cursor, limit, locale, apiBaseUrl } = args;
  const inputObj: Record<string, unknown> = { slug, limit };
  if (typeof cursor === "number") inputObj.cursor = cursor;
  const encoded = encodeURIComponent(JSON.stringify({ json: inputObj }));
  const url = `${buildApiUrl(apiBaseUrl)}?input=${encoded}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { "x-locale": locale, accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`timeline.list failed: ${res.status}`);
  const json = (await res.json()) as {
    result?: { data?: { json?: TimelineListOutput } | TimelineListOutput };
  };
  const inner = json.result?.data;
  const data =
    (inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: TimelineListOutput }).json
      : (inner as TimelineListOutput | undefined)) ?? undefined;
  if (!data) throw new Error("timeline.list: malformed response");
  return {
    nextCursor: data.nextCursor,
    events: data.events.map((e) => ({
      ...e,
      occurredAt: new Date(e.occurredAt as unknown as string),
      verifiedAt: e.verifiedAt ? new Date(e.verifiedAt as unknown as string) : null,
    })),
  };
}

export function Timeline(props: TimelineProps) {
  return (
    <MotionConfig reducedMotion="user">
      <TimelineInner {...props} />
    </MotionConfig>
  );
}

function TimelineInner({ slug, locale, t, limit = 30, initialItems, apiBaseUrl }: TimelineProps) {
  const reducedMotion = useReducedMotion();

  const query = useInfiniteQuery<TimelineListOutput, Error>({
    queryKey: ["timeline.list", { slug, locale, limit }],
    queryFn: ({ pageParam }) =>
      fetchTimelinePage({
        slug,
        cursor: pageParam as number | undefined,
        limit,
        locale,
        apiBaseUrl,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined,
    initialData: initialItems ? { pages: [initialItems], pageParams: [undefined] } : undefined,
    staleTime: 60_000,
  });

  const events: TimelineEventOut[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.events) ?? [],
    [query.data],
  );

  const isInitialLoading = query.isLoading && events.length === 0;
  const isEmpty = !isInitialLoading && events.length === 0;

  return (
    <section
      id="timeline"
      aria-label={t("timeline.ariaLabel")}
      className="timeline"
      style={{ display: "grid", gap: "1rem" }}
    >
      <h2
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "1.25rem",
          fontWeight: 600,
          color: "var(--color-fg-default)",
        }}
      >
        {t("timeline.title")}
      </h2>

      {isInitialLoading ? (
        <output
          aria-label={t("timeline.loading")}
          style={{
            display: "block",
            paddingBlock: "2rem",
            textAlign: "center",
            color: "var(--color-fg-muted)",
            fontSize: "0.875rem",
          }}
        >
          {t("timeline.loading")}
        </output>
      ) : null}

      {isEmpty ? (
        <p
          style={{
            margin: 0,
            paddingBlock: "1.5rem",
            textAlign: "center",
            color: "var(--color-fg-muted)",
          }}
        >
          {t("timeline.empty")}
        </p>
      ) : null}

      <ol
        className="timeline-list"
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          position: "relative",
          display: "grid",
          gap: "1.25rem",
        }}
      >
        {/* Centre line — drawn behind all events. Stays vertical in RTL. */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            insetBlockStart: 0,
            insetBlockEnd: 0,
            insetInlineStart: "0.6875rem",
            inlineSize: "1px",
            background: "var(--color-border-default)",
          }}
          className="timeline-axis"
        />

        {events.map((event, i) => (
          <TimelineEvent
            key={event.id}
            event={event}
            index={i}
            locale={locale}
            t={t}
            reducedMotion={Boolean(reducedMotion)}
          />
        ))}
      </ol>

      {query.hasNextPage ? (
        <button
          type="button"
          onClick={() => void query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
          style={{
            justifySelf: "center",
            minHeight: "44px",
            paddingInline: "1.25rem",
            paddingBlock: "0.625rem",
            borderRadius: "0.375rem",
            background: "transparent",
            color: "var(--color-fg-default)",
            border: "1px solid var(--color-border-default)",
            fontFamily: "var(--font-mono)",
            fontSize: "0.8125rem",
            cursor: query.isFetchingNextPage ? "default" : "pointer",
            opacity: query.isFetchingNextPage ? 0.6 : 1,
            transition: "background-color 150ms ease",
          }}
        >
          {query.isFetchingNextPage ? t("timeline.loadingMore") : t("timeline.loadOlder")}
        </button>
      ) : null}

      {query.isError ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: "0.75rem 1rem",
            border: "1px solid var(--color-accent-emergency)",
            background: "hsl(354 78% 52% / 0.08)",
            color: "var(--color-accent-emergency)",
            borderRadius: "0.375rem",
            fontSize: "0.875rem",
          }}
        >
          {t("timeline.error")}
        </p>
      ) : null}
    </section>
  );
}

function TimelineEvent({
  event,
  index,
  locale,
  t,
  reducedMotion,
}: {
  event: TimelineEventOut;
  index: number;
  locale: string;
  t: TranslateFn;
  reducedMotion: boolean;
}) {
  const dateLabel = formatDate(event.occurredAt, locale, { dateStyle: "medium" });
  const colour = SEVERITY_TOKEN[event.severity] ?? SEVERITY_TOKEN.info;
  // Even rows on the visual left side, odd rows on the visual right side.
  // The content inside cards still uses logical text flow for RTL locales.
  const onStart = index % 2 === 0;

  const initial = reducedMotion ? "visible" : "hidden";

  return (
    <motion.li
      data-testid="timeline-event"
      data-severity={event.severity}
      initial={initial}
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={EVENT_VARIANTS}
      style={{
        position: "relative",
        paddingInlineStart: "1.75rem",
        display: "grid",
      }}
      className={
        onStart ? "timeline-event timeline-event--start" : "timeline-event timeline-event--end"
      }
    >
      {/* Axis dot. Rounded-full allowed for status dots. */}
      <span
        aria-hidden="true"
        className="timeline-event-dot"
        style={{
          position: "absolute",
          insetInlineStart: "0.375rem",
          insetBlockStart: "0.375rem",
          inlineSize: "0.625rem",
          blockSize: "0.625rem",
          borderRadius: "50%",
          background: colour,
          boxShadow: "0 0 0 2px var(--color-bg-base)",
        }}
      />

      <div
        className="timeline-event-content"
        style={{
          display: "grid",
          gap: "0.375rem",
          inlineSize: "100%",
          padding: "1rem",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "0.5rem",
          background: "var(--color-bg-surface)",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.5rem",
            fontSize: "0.75rem",
            fontFamily: "var(--font-mono)",
            color: "var(--color-fg-muted)",
          }}
        >
          <span
            className="timeline-date-pill"
            style={{
              display: "inline-flex",
              paddingInline: "0.5rem",
              paddingBlock: "0.125rem",
              borderRadius: "0.25rem",
              background: "var(--color-bg-surface)",
              border: "1px solid var(--color-border-subtle)",
              color: "var(--color-fg-default)",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            {dateLabel}
          </span>
          <span
            aria-label={t(`timeline.severity.${event.severity}`)}
            style={{
              color: colour,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.6875rem",
              fontWeight: 700,
            }}
          >
            {t(`timeline.severity.${event.severity}`)}
          </span>
        </div>

        <h3
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: "1rem",
            fontWeight: 600,
            color: "var(--color-fg-default)",
            lineHeight: 1.3,
          }}
        >
          {event.title}
        </h3>

        {event.body ? (
          <div
            style={{
              margin: 0,
              fontSize: "0.875rem",
              lineHeight: 1.55,
              color: "var(--color-fg-muted)",
            }}
          >
            <Markdown source={event.body} />
          </div>
        ) : null}

        {event.tags && event.tags.length > 0 ? (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexWrap: "wrap",
              gap: "0.375rem",
              marginBlockStart: "0.125rem",
            }}
          >
            {event.tags.map((tag) => (
              <li
                key={tag}
                style={{
                  fontSize: "0.6875rem",
                  fontFamily: "var(--font-mono)",
                  paddingInline: "0.5rem",
                  paddingBlock: "0.125rem",
                  borderRadius: "0.25rem",
                  background: "var(--color-bg-surface)",
                  color: "var(--color-fg-muted)",
                  border: "1px solid var(--color-border-subtle)",
                }}
              >
                #{tag}
              </li>
            ))}
          </ul>
        ) : null}

        {event.sourceUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <a
              href={event.sourceUrl}
              target="_blank"
              rel="noopener noreferrer external"
              style={{
                display: "inline-flex",
                minInlineSize: 44,
                minBlockSize: 44,
                alignItems: "center",
                fontSize: "0.75rem",
                fontFamily: "var(--font-mono)",
                color: "var(--color-accent-info)",
                textDecoration: "underline",
              }}
            >
              {t("timeline.viewSource")}
            </a>
            {event.sourceUrl && event.verifiedAt ? (
              <SourceCitation
                sources={[
                  {
                    url: event.sourceUrl,
                    label: event.sourceLabel ?? t("citations.label"),
                    verifiedAt: (event.verifiedAt as Date).toISOString(),
                  } satisfies Source,
                ]}
                locale={locale}
                t={t}
                siblingGlyph={false}
              >
                <span />
              </SourceCitation>
            ) : null}
          </div>
        ) : null}
      </div>
    </motion.li>
  );
}

// ---------------------------------------------------------------------------
// Tiny safe Markdown renderer. Public so tests can assert behaviour.
// ---------------------------------------------------------------------------

const LINK_STYLE: CSSProperties = {
  color: "var(--color-accent-info)",
  textDecoration: "underline",
};
const CODE_STYLE: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "0.875em",
  background: "var(--color-bg-surface-elevated)",
  paddingInline: "0.25rem",
  paddingBlock: "0.0625rem",
  borderRadius: "0.1875rem",
};

/** Render a small subset of markdown inline. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  // Order matters: code spans first (so we don't confuse `*` inside them),
  // then links, then bold, then italic.
  const out: ReactNode[] = [];
  let cursor = 0;
  let i = 0;
  // Combined regex captures the *kind* of token via named groups.
  const re = /(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;
  let match = re.exec(text);
  while (match !== null) {
    if (match.index > cursor) {
      out.push(text.slice(cursor, match.index));
    }
    if (match[2]) {
      out.push(
        <code key={`${keyPrefix}-c-${i++}`} style={CODE_STYLE}>
          {match[2]}
        </code>,
      );
    } else if (match[3]) {
      const safeHref = sanitizeHref(match[5] ?? "");
      out.push(
        <a
          key={`${keyPrefix}-a-${i++}`}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          style={LINK_STYLE}
        >
          {match[4]}
        </a>,
      );
    } else if (match[6]) {
      out.push(<strong key={`${keyPrefix}-b-${i++}`}>{match[7]}</strong>);
    } else if (match[8]) {
      out.push(<em key={`${keyPrefix}-i-${i++}`}>{match[9]}</em>);
    }
    cursor = match.index + match[0].length;
    match = re.exec(text);
  }
  if (cursor < text.length) {
    out.push(text.slice(cursor));
  }
  return out;
}

function sanitizeHref(href: string): string {
  const trimmed = href.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("mailto:")
  ) {
    return trimmed;
  }
  return "#";
}

export function Markdown({ source }: { source: string }): ReactElement {
  const blocks = useMemo(() => parseMarkdownBlocks(source), [source]);
  return (
    <Fragment>
      {blocks.map((block, idx) => {
        // Block key combines index + content prefix for stable identity:
        // markdown blocks are derived from a memoized parse, so the
        // (idx, content) pair is the natural primary key.
        const blockKey =
          block.type === "p"
            ? `p-${idx}-${block.text.slice(0, 16)}`
            : `${block.type}-${idx}-${block.items.length}`;
        if (block.type === "ul") {
          return (
            <ul key={blockKey} style={{ margin: "0 0 0 1rem", paddingInlineStart: "1rem" }}>
              {block.items.map((it, i) => (
                <li key={`${blockKey}-i${i}-${it.slice(0, 12)}`}>
                  {renderInline(it, `b${idx}i${i}`)}
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={blockKey} style={{ margin: "0 0 0 1rem", paddingInlineStart: "1rem" }}>
              {block.items.map((it, i) => (
                <li key={`${blockKey}-i${i}-${it.slice(0, 12)}`}>
                  {renderInline(it, `b${idx}i${i}`)}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={blockKey} style={{ margin: "0 0 0.5rem 0" }}>
            {renderInline(block.text, `b${idx}`)}
          </p>
        );
      })}
    </Fragment>
  );
}

type MdBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

export function parseMarkdownBlocks(source: string): MdBlock[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: MdBlock[] = [];
  let buf: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (buf.length === 0) return;
    blocks.push({ type: "p", text: buf.join(" ").trim() });
    buf = [];
  };
  const flushList = () => {
    if (listType && listItems.length) {
      blocks.push({ type: listType, items: listItems });
    }
    listType = null;
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "") {
      flushParagraph();
      flushList();
      continue;
    }
    const ulMatch = /^[-*]\s+(.*)$/.exec(trimmed);
    const olMatch = /^\d+\.\s+(.*)$/.exec(trimmed);
    if (ulMatch) {
      flushParagraph();
      if (listType !== "ul") flushList();
      listType = "ul";
      listItems.push(ulMatch[1] ?? "");
      continue;
    }
    if (olMatch) {
      flushParagraph();
      if (listType !== "ol") flushList();
      listType = "ol";
      listItems.push(olMatch[1] ?? "");
      continue;
    }
    flushList();
    buf.push(trimmed);
  }
  flushParagraph();
  flushList();
  return blocks;
}
